import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, MapPin, Wallet, Clock, X } from "lucide-react";
import { TaskChatOnlyEnhanced } from "./TaskChatOnlyEnhanced";
import { CancelWithRefundButton } from "./CancelWithRefundButton";
import { TaskStatusBadge } from "./TaskStatusBadge";
import { useSwipeDown } from "../../utils/useSwipeDown";
import { useSingleFlight } from "../../utils/singleFlight";
import { needsSurcharge, fmtRub } from "../../utils/priceChange";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ж20: причина неактивности «Принять» — proposed/доплата + серверная причина
export const acceptBlock = (task, pc) => {
  const p = pc !== undefined ? pc : task.active_price_change;
  if (p?.status === "proposed" || task.active_price_change?.status === "proposed") {
    return { kind: "proposed", text: "Дождитесь согласования новой цены и доплаты заказчиком: сначала ответьте на предложение цены в чате." };
  }
  if (needsSurcharge(task, p)) {
    return { kind: "surcharge", text: `Дождитесь согласования новой цены и доплаты заказчиком: принятие откроется после оплаты доплаты (${fmtRub(p.new_price - p.old_price)} ₽).` };
  }
  if (task.accept_block_reason) return { kind: "server", text: task.accept_block_reason };
  return null;
};

export const participantRole = (task, user) => {
  if (task.customer_id === user.id) return "заказчик";
  if (task.executor_id === user.id) return "исполнитель";
  if (task.discussing_executor_id === user.id) return "обсуждающий";
  return "участник";
};

const useActivePriceChange = (taskId) => {
  const [pc, setPc] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/tasks/${taskId}/active-price-change`)
      .then((r) => setPc(r.data)).catch((e) => { if (e.response?.status === 404) setPc(null); });
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [taskId]);
  return pc;
};

// TZ8-6: единая чат-модаль (эталон раздела «Чат»): «Чат», витрина после «Ознакомиться», карточка «Мои задания»
export const TaskChatModal = ({ task, user, showToast, onClose, onChanged, onAccepted, onNewMessage, testId = "discussion-chat-modal" }) => {
  const { dragHandleRef, sheetStyle } = useSwipeDown({ onClose });
  const { run, isBusy } = useSingleFlight();
  const pc = useActivePriceChange(task.id);
  const isExec = user.role === "executor" && task.status === "discussing" && task.discussing_executor_id === user.id;
  const isCust = (user.role === "customer" || user.role === "admin") && task.status === "discussing" && task.customer_id === user.id;
  const block = isExec ? acceptBlock(task, pc) : null;
  const acceptPrice = pc?.status === "confirmed" ? pc.new_price : task.price;
  const done = () => { onClose(); if (onChanged) onChanged(); };

  const post = (url, okText, errText) => async () => {
    try {
      await axios.post(url);
      showToast(okText, "info");
      done();
    } catch (error) {
      showToast(error.response?.data?.detail || errText, "error");
    }
  };

  const accept = () => !block && run(`accept:${task.id}`, async () => {
    try {
      await axios.post(`${API}/tasks/${task.id}/accept`);
      showToast("Задание принято!", "success");
      if (onAccepted) onAccepted();
      done();
    } catch (error) {
      showToast(error.response?.data?.detail || "Не удалось принять задание. Попробуйте ещё раз.", "error");
    }
  });

  const decline = () => window.confirm("Вы уверены что хотите отказаться от этого задания?")
    && post(`${API}/tasks/${task.id}/cancel-discussion`, "Вы отказались от задания", "Не удалось отказаться от задания. Попробуйте ещё раз.")();
  const cancelDiscussion = () => window.confirm("Отменить обсуждение с этим исполнителем? Задание станет доступно другим.")
    && post(`${API}/tasks/${task.id}/cancel-discussion`, "Обсуждение отменено", "Не удалось отменить обсуждение. Попробуйте ещё раз.")();

  const actions = (isExec || isCust) ? (
    <>
      {isExec && needsSurcharge(task, pc) && (
        <div className="rv-span-2 flex items-start gap-2.5 px-3 py-2.5 rounded-xl" data-testid="executor-surcharge-waiting" style={{background:'#FFFBEB', border:'1px solid #FDE68A'}}>
          <Clock size={16} className="flex-shrink-0 mt-0.5" style={{color:'#B45309'}} />
          <div className="text-xs" style={{color:'#92400E'}}>
            <p className="text-sm font-semibold">Ожидаем оплату доплаты заказчиком</p>
            <p>Вы согласились на новую цену: <strong>{fmtRub(pc.new_price)} ₽</strong> (доплата +{fmtRub(pc.new_price - pc.old_price)} ₽). Принятие откроется после оплаты доплаты заказчиком.</p>
          </div>
        </div>
      )}
      {isExec && block && block.kind === 'server' && (
        <p className="rv-span-2 text-xs px-3 py-2 rounded-xl" data-testid="chat-accept-blocked-hint" style={{background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE'}}>{block.text}</p>
      )}
      {isExec && (
        <button data-testid="chat-accept-task-btn" onClick={accept} disabled={!!block || isBusy(`accept:${task.id}`)} aria-disabled={!!block}
          title={block ? block.text : undefined} className="reversia-btn reversia-btn-green reversia-btn-md justify-center"
          style={block ? {opacity:0.5, cursor:'not-allowed'} : undefined}>Принять за {fmtRub(acceptPrice)} ₽</button>
      )}
      {isExec && <button data-testid="chat-decline-task-btn" onClick={decline} className="reversia-btn reversia-btn-quiet reversia-btn-md justify-center">Отказаться</button>}
      {isCust && <button data-testid="chat-cancel-discussion-btn" onClick={cancelDiscussion} className="reversia-btn reversia-btn-quiet reversia-btn-md justify-center">Отменить обсуждение</button>}
      {isCust && <CancelWithRefundButton task={task} showToast={showToast} testId="chat-cancel-task-btn" onDone={done} />}
    </>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{zIndex:300}} data-testid={testId}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-4xl h-full sm:h-[90vh] flex flex-col overflow-hidden shadow-2xl" style={{maxHeight:'100dvh', ...sheetStyle}}>
        <div ref={dragHandleRef} className="sm:hidden flex-shrink-0 flex justify-center items-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full" style={{background:'#D1D5DB'}} />
        </div>
        <ChatModalHeader task={task} user={user} onClose={onClose} />
        <div className="flex-1 min-h-0 flex flex-col">
          <TaskChatOnlyEnhanced task={task} user={user} showToast={showToast} onNewMessage={onNewMessage} actions={actions} />
        </div>
      </div>
    </div>
  );
};

const ChatModalHeader = ({ task, user, onClose }) => {
  const coords = task.location?.startsWith('Координаты:');
  return (
    <div className="flex-shrink-0 flex items-start justify-between gap-2 px-3 py-2 sm:px-4 sm:py-2.5 border-b" data-testid="chat-modal-header" style={{background:'var(--rv-white)', borderColor:'var(--rv-border)'}}>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm sm:text-base font-semibold flex items-center gap-1.5 min-w-0 leading-tight" data-testid="chat-modal-title" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
          <MessageSquare size={15} style={{color:'var(--rv-teal)'}} className="flex-shrink-0" />
          <span className="truncate sm:whitespace-normal sm:line-clamp-2" title={task.title}>{task.title}</span>
        </h3>
        <div className="mt-0.5 flex items-center gap-x-2 gap-y-0.5 flex-wrap text-xs leading-tight" data-testid="chat-modal-meta" style={{color:'var(--rv-text-secondary)'}}>
          <span className="inline-flex items-center gap-1 min-w-0">
            <MapPin size={12} className="flex-shrink-0" style={{color:'var(--rv-text-muted)'}} />
            <span className="truncate max-w-[220px] sm:max-w-none" style={{color: coords ? 'var(--rv-teal)' : 'inherit', fontWeight: coords ? 600 : 'normal'}}>{task.location}</span>
          </span>
          <span className="inline-flex items-center gap-1 font-semibold" data-testid="chat-modal-price" style={{color:'var(--rv-navy)'}}><Wallet size={12} style={{color:'var(--rv-text-muted)'}} />{fmtRub(task.price)} ₽</span>
          <TaskStatusBadge task={task} />
          <span className="hidden sm:inline">Вы: {participantRole(task, user)}</span>
        </div>
      </div>
      <button data-testid="chat-modal-close-btn" onClick={onClose} className="flex-shrink-0 rounded-md p-0.5" style={{background:'none', border:'none', cursor:'pointer', color:'var(--rv-text-muted)'}} aria-label="Закрыть">
        <X size={20} />
      </button>
    </div>
  );
};
